/**
 * NotificationItem Component
 * Displays a single notification with appropriate styling and actions
 */
import React from 'react';
import {
  Box,
  Typography,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  AssignmentInd as AssignmentIndIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  SyncAlt as SyncAltIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ErrorOutline as ErrorOutlineIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  DoneAll as DoneAllIcon,
  TaskAlt as TaskAltIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  AccessAlarm as AccessAlarmIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

/**
 * Get icon based on notification type
 */
const getNotificationIcon = (type) => {
  const iconMap = {
    // Ticket-related events
    TICKET_CREATION: <AddCircleOutlineIcon />,
    TICKET_ASSIGNMENT: <AssignmentIndIcon />,
    TICKET_STATUS_CHANGE: <SyncAltIcon />,
    TICKET_COMPLETION: <CheckCircleIcon />,
    TICKET_REJECTION: <CancelIcon />,
    TICKET_INCOMPLETE: <ErrorOutlineIcon />,

    // Workflow-related events
    WORKFLOW_INITIATION: <PlayCircleOutlineIcon />,
    WORKFLOW_STEP_COMPLETION: <DoneAllIcon />,
    WORKFLOW_COMPLETION: <TaskAltIcon />,

    // Other events
    COMMENT_ADDED: <ChatBubbleOutlineIcon />,
    REMINDER_DUE: <AccessAlarmIcon />,

    DEFAULT: <NotificationsIcon />
  };

  return iconMap[type] || iconMap.DEFAULT;
};

/**
 * Get color based on notification type
 */
const getNotificationColor = (type) => {
  const colorMap = {
    // Ticket-related
    TICKET_CREATION: '#1976d2',
    TICKET_ASSIGNMENT: '#0288d1',
    TICKET_STATUS_CHANGE: '#0288d1',
    TICKET_COMPLETION: '#2e7d32',
    TICKET_REJECTION: '#d32f2f',
    TICKET_INCOMPLETE: '#ed6c02',

    // Workflow-related
    WORKFLOW_INITIATION: '#5e35b1',
    WORKFLOW_STEP_COMPLETION: '#43a047',
    WORKFLOW_COMPLETION: '#1b5e20',

    // Other events
    COMMENT_ADDED: '#0097a7',
    REMINDER_DUE: '#f9a825',

    DEFAULT: '#757575'  
  };

  return colorMap[type] || colorMap.DEFAULT;
};

/**
 * Get priority color
 */
const getPriorityColor = (priority) => {
  const colorMap = {
    HIGH: 'error',
    NORMAL: 'warning',
    LOW: 'info',
    URGENT: 'error'
  };

  return colorMap[priority] || 'default';
};

const NotificationItem = ({ notification, onMarkAsRead, onClick }) => {
  const { id, title, message, type, read, createdAt, data } = notification;
  
  // Parse data if it's a string
  let parsedData = data;
  try {
    if (typeof data === 'string') {
      parsedData = JSON.parse(data);
    }
  } catch (error) {
    console.error('Error parsing notification data:', error);
    parsedData = {};
  }
  
  const handleClick = () => {
    if (!read && onMarkAsRead) {
      onMarkAsRead(id);
    }
    if (onClick) {
      onClick(notification);
    }
  };

  const timeAgo = createdAt 
    ? formatDistanceToNow(new Date(createdAt), { addSuffix: true })
    : '';

  return (
    <ListItem
      disablePadding
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': {
          borderBottom: 'none'
        }
      }}
    >
      <ListItemButton
        onClick={handleClick}
        sx={{
          py: 1.5,
          px: 2,
          backgroundColor: read ? 'transparent' : 'action.hover',
          '&:hover': {
            backgroundColor: read ? 'action.hover' : 'action.selected'
          },
          transition: 'background-color 0.2s ease'
        }}
      >
        <ListItemAvatar>
          <Avatar
            sx={{
              bgcolor: getNotificationColor(type),
              width: 40,
              height: 40
            }}
          >
            {getNotificationIcon(type)}
          </Avatar>
        </ListItemAvatar>
        
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: read ? 400 : 600,
                  color: 'text.primary',
                  flex: 1
                }}
              >
                {title}
              </Typography>
              {!read && (
                <Tooltip title="Unread">
                  <CircleIcon
                    sx={{
                      fontSize: 10,
                      color: 'primary.main'
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          }
          secondary={
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mb: 0.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {message}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                    fontSize: '0.75rem'
                  }}
                >
                  {timeAgo}
                </Typography>
                
                {parsedData?.priority && (
                  <Chip
                    label={parsedData.priority}
                    size="small"
                    color={getPriorityColor(parsedData.priority)}
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 500
                    }}
                  />
                )}
                
                {parsedData?.ticketNumber && (
                  <Chip
                    label={parsedData.ticketNumber}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem'
                    }}
                  />
                )}
              </Box>
            </Box>
          }
        />
      </ListItemButton>
    </ListItem>
  );
};

export default NotificationItem;
